import clsx from "clsx";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

// ------------------------------- CONSTANTS -------------------------------
const CLASSES_POR_VARIANTE = {
	soft: {
		muted: "border-border/60 bg-muted text-muted-foreground",
		primary: "border-primary/25 bg-primary/10 text-primary",
		secondary: "border-secondary/25 bg-secondary/10 text-secondary",
		success: "border-success/25 bg-success/10 text-success",
		warning: "border-warning/25 bg-warning/10 text-warning",
		danger: "border-danger/25 bg-danger/10 text-danger",
		info: "border-info/25 bg-info/10 text-info",
	},
	solid: {
		muted: "border-foreground bg-foreground text-background",
		primary: "border-primary bg-primary text-primary-foreground",
		secondary: "border-secondary bg-secondary text-secondary-foreground",
		success: "border-success bg-success text-success-foreground",
		warning: "border-warning bg-warning text-warning-foreground",
		danger: "border-danger bg-danger text-danger-foreground",
		info: "border-info bg-info text-info-foreground",
	},
	outline: {
		muted: "border-border bg-transparent text-muted-foreground",
		primary: "border-primary/40 bg-transparent text-primary",
		secondary: "border-secondary/40 bg-transparent text-secondary",
		success: "border-success/40 bg-transparent text-success",
		warning: "border-warning/40 bg-transparent text-warning",
		danger: "border-danger/40 bg-transparent text-danger",
		info: "border-info/40 bg-transparent text-info",
	},
} as const;

const CLASSES_POR_TAMANHO = {
	compact: "min-h-4 px-1 py-0 text-[10px]",
	small: "min-h-5 px-2 py-0.5 text-[11px]",
	medium: "min-h-6 px-2.5 py-1 text-xs",
} as const;

// --------------------------------- TYPES ---------------------------------
export type AppBadgeVariante = keyof typeof CLASSES_POR_VARIANTE;
export type AppBadgeTom = keyof (typeof CLASSES_POR_VARIANTE)["soft"];
export type AppBadgeTamanho = keyof typeof CLASSES_POR_TAMANHO;

export type AppBadgeProps = Omit<ComponentPropsWithoutRef<"span">, "children"> & {
	readonly children: ReactNode;
	readonly icone?: ReactNode;
	readonly color?: AppBadgeTom;
	readonly variante?: AppBadgeVariante;
	readonly tamanho?: AppBadgeTamanho;
};

// ------------------------------- COMPONENT -------------------------------
export default function AppBadge({
	children,
	icone,
	color = "muted",
	variante = "soft",
	tamanho = "small",
	className,
	...spanProps
}: Readonly<AppBadgeProps>) {
	return (
		<span
			{...spanProps}
			className={clsx(
				"inline-flex max-w-full shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full border font-semibold leading-none",
				CLASSES_POR_VARIANTE[variante][color],
				CLASSES_POR_TAMANHO[tamanho],
				className,
			)}
		>
			{icone}
			{children}
		</span>
	);
}
